import React, { useState, useCallback } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  FlatList,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useMutation, useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { fontFamily } from "../constants/fonts";
import { colors } from "../constants/theme";

/**
 * Pick a list to save a restaurant to, or create a new list. Private lists only for now.
 */
export default function SaveToListModal({ visible, onClose, restaurantId, privyDid }) {
  const [newListName, setNewListName] = useState("");
  const [busyListId, setBusyListId] = useState(null);

  const lists = useQuery(
    api.savedLists.listMine,
    privyDid && visible ? { privyDid } : "skip"
  );
  const containingIds = useQuery(
    api.savedLists.listsContainingRestaurant,
    privyDid && visible && restaurantId ? { privyDid, restaurantId } : "skip"
  );

  const createList = useMutation(api.savedLists.createList);
  const addRestaurantToList = useMutation(api.savedLists.addRestaurantToList);
  const removeRestaurantFromList = useMutation(api.savedLists.removeRestaurantFromList);

  const handleCreateList = useCallback(async () => {
    const name = newListName.trim();
    if (!name || !privyDid) return;
    try {
      await createList({ privyDid, name });
      setNewListName("");
    } catch (e) {
      Alert.alert("Could not create list", e?.message || "Try again.");
    }
  }, [createList, newListName, privyDid]);

  const handleAddToList = useCallback(
    async (listId) => {
      if (!privyDid || !restaurantId) return;
      setBusyListId(listId);
      try {
        const res = await addRestaurantToList({
          privyDid,
          listId,
          restaurantId,
        });
        if (res?.success === false && res?.error === "already_in_list") {
          Alert.alert("Already saved", "This place is already on that list.");
        } else {
          onClose?.();
        }
      } catch (e) {
        Alert.alert("Could not save", e?.message || "Try again.");
      } finally {
        setBusyListId(null);
      }
    },
    [addRestaurantToList, privyDid, restaurantId, onClose]
  );

  const handleRemoveFromList = useCallback(
    async (listId) => {
      if (!privyDid || !restaurantId) return;
      setBusyListId(listId);
      try {
        await removeRestaurantFromList({ privyDid, listId, restaurantId });
      } catch (e) {
        Alert.alert("Could not remove", e?.message || "Try again.");
      } finally {
        setBusyListId(null);
      }
    },
    [removeRestaurantFromList, privyDid, restaurantId]
  );

  const hasContaining = (id) =>
    Array.isArray(containingIds) &&
    containingIds.some((x) => String(x) === String(id));

  const isOnAnyList =
    Array.isArray(containingIds) && containingIds.length > 0;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      presentationStyle={Platform.OS === "ios" ? "overFullScreen" : undefined}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1, justifyContent: "flex-end", backgroundColor: "rgba(0,0,0,0.45)" }}
      >
        <TouchableOpacity style={{ flex: 1 }} activeOpacity={1} onPress={onClose} />
        <View
          style={{
            backgroundColor: colors.cream,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingHorizontal: 20,
            paddingTop: 12,
            paddingBottom: 28,
            maxHeight: "72%",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.abhayaBold,
                fontSize: 22,
                color: colors.black,
              }}
            >
              {isOnAnyList ? "Saved lists" : "Save to list"}
            </Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Ionicons name="close" size={28} color={colors.disgoBrown} />
            </TouchableOpacity>
          </View>
          <Text
            style={{
              fontFamily: fontFamily.abhaya,
              fontSize: 14,
              color: colors.gray600,
              marginBottom: 12,
            }}
          >
            Only you can see your lists. Tap a list to add this place; tap again to remove it from that list.
          </Text>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <TextInput
              value={newListName}
              onChangeText={setNewListName}
              placeholder="New list name"
              placeholderTextColor={colors.gray500}
              style={{
                flex: 1,
                marginRight: 8,
                borderWidth: 1,
                borderColor: colors.gray300,
                borderRadius: 10,
                paddingHorizontal: 12,
                paddingVertical: 10,
                fontFamily: fontFamily.abhaya,
                fontSize: 16,
                color: colors.black,
              }}
            />
            <TouchableOpacity
              onPress={handleCreateList}
              disabled={!newListName.trim()}
              style={{
                backgroundColor: colors.disgoBrown,
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 10,
                opacity: newListName.trim() ? 1 : 0.45,
              }}
            >
              <Text style={{ color: colors.white, fontFamily: fontFamily.abhayaBold }}>
                Create
              </Text>
            </TouchableOpacity>
          </View>

          {lists === undefined ? (
            <ActivityIndicator size="large" color={colors.disgoBrown} style={{ marginVertical: 24 }} />
          ) : lists.length === 0 ? (
            <Text style={{ fontFamily: fontFamily.abhaya, color: colors.gray600, textAlign: "center", marginVertical: 16 }}>
              No lists yet. Create one above.
            </Text>
          ) : (
            <FlatList
              data={lists}
              keyExtractor={(item) => item._id}
              style={{ maxHeight: 320 }}
              renderItem={({ item }) => {
                const inList = hasContaining(item._id);
                const busy = String(busyListId) === String(item._id);
                return (
                  <TouchableOpacity
                    onPress={() => {
                      if (busy) return;
                      if (inList) handleRemoveFromList(item._id);
                      else handleAddToList(item._id);
                    }}
                    disabled={busy}
                    accessibilityLabel={
                      inList
                        ? `Remove from ${item.name}`
                        : `Add to ${item.name}`
                    }
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingVertical: 14,
                      borderBottomWidth: 1,
                      borderBottomColor: colors.gray200,
                    }}
                  >
                    <View style={{ flex: 1, paddingRight: 8 }}>
                      <Text
                        style={{
                          fontFamily: fontFamily.abhayaBold,
                          fontSize: 17,
                          color: colors.black,
                        }}
                      >
                        {item.name}
                      </Text>
                      <Text style={{ fontFamily: fontFamily.abhaya, fontSize: 13, color: colors.gray600 }}>
                        {item.itemCount} {item.itemCount === 1 ? "place" : "places"}
                      </Text>
                    </View>
                    {busy ? (
                      <ActivityIndicator size="small" color={colors.disgoBrown} />
                    ) : inList ? (
                      <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <Ionicons name="checkmark-circle" size={22} color={colors.disgoBrown} />
                        <Text style={{ marginLeft: 4, fontFamily: fontFamily.abhayaBold, color: colors.disgoBrown }}>
                          Saved
                        </Text>
                      </View>
                    ) : (
                      <Ionicons name="add-circle-outline" size={26} color={colors.disgoBrown} />
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          )}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
